/**
 * Activation copy — the SENTENCES an account's resolved money picture is
 * stated in. Shared by `pages/WelcomePage.tsx`, `pages/PricingPage.tsx`, and
 * `lib/activation-incentive.ts` so the surfaces can never phrase the same
 * facts differently.
 *
 * There is no arithmetic in this file, deliberately. It used to compute the
 * effective monthly price and walk a starter credit into covered months — a
 * TypeScript reimplementation of rules the backend already owned, which
 * drifted into promising "first 3 months on us" where the invoices paid two
 * (a credit outliving its discount window buys months at LIST price, but the
 * old code divided by the discounted rate forever).
 *
 * The boundary now: `crmbilling.ResolveActivationPricing` owns the NUMBERS
 * and ships them in the `activation` block; this file owns the WORDS. That is
 * why the covered-months fact is not rendered server-side the way the
 * marketplace fee line is — it appears in four different phrasings across
 * subtitle, body, banner and chip, and an integer has one meaning where
 * "first 2 months on us" has four wordings.
 *
 * The three copy branches, in order:
 *
 *  - the credit covers a whole month or more → the "first N months on us"
 *    claim, naming how many months it REALLY buys.
 *  - 0 < promised, covering less than one month → an honest partial-credit
 *    claim.
 *  - no confirmed promise → plain pricing, no free-month claim of any kind.
 */
import type { CRMBillingActivationVM } from "./view-model";

// Renders cents. A whole-dollar amount drops the ".00" ($49), a fractional one
// keeps both places ($24.50) — the founding half-price figure is exactly the
// case toFixed(0) used to round away inside the promise copy itself.
export const money = (cents: number) => {
  const dollars = cents / 100;
  return `$${dollars.toLocaleString("en-US", {
    minimumFractionDigits: Number.isInteger(dollars) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * The lowercase mid-sentence claim — "first month on us", "first 2 months on
 * us", "first 2 months on us, plus part of the next". Callers uppercase the
 * first letter for standalone/subtitle use.
 *
 * Only meaningful at `starterCoversMonths >= 1`. The "plus part of the next"
 * tail is what keeps a 1.5-month credit from being rounded up into "two
 * months free" — a promise the invoice doesn't keep.
 */
export function coveredMonthsClause(
  activation: Pick<
    CRMBillingActivationVM,
    "starterCoversMonths" | "starterHasRemainder"
  >,
): string {
  const whole =
    activation.starterCoversMonths === 1
      ? "first month on us"
      : `first ${activation.starterCoversMonths} months on us`;
  return activation.starterHasRemainder
    ? `${whole}, plus part of the next`
    : whole;
}

const sentenceCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The monthly-lane activation copy. Both the price and the covered-months
 * count come straight off `activation` — this function decides wording only.
 */
export function starterActivationCopy(
  activation: CRMBillingActivationVM,
  starterPromisedCents: number,
): { subtitle: string; body: string } {
  const price = money(activation.effectiveMonthlyCents);
  // `starterPromisedCents > 0` is belt-and-braces: server-side the covered
  // months are DERIVED from the promise, so a positive count already implies
  // one. Asserting it here means no combination of inputs — including a stale
  // or hand-built block — can produce a free-month claim for an account that
  // was promised nothing.
  if (
    starterPromisedCents > 0 &&
    activation.effectiveMonthlyCents > 0 &&
    activation.starterCoversMonths >= 1
  ) {
    const clause = coveredMonthsClause(activation);
    return {
      subtitle: sentenceCase(clause),
      body: `Sill Pro — ${price}/mo, ${clause}, card required, cancel anytime.`,
    };
  }
  if (starterPromisedCents > 0) {
    return {
      subtitle: `${money(starterPromisedCents)} credit toward your first month`,
      body: `Sill Pro — ${price}/mo, card required, cancel anytime. Includes a ${money(starterPromisedCents)} credit toward your first month.`,
    };
  }
  return {
    subtitle: "Card required, cancel anytime",
    body: `Sill Pro — ${price}/mo, card required, cancel anytime.`,
  };
}

/**
 * F3: the annual-lane counterpart. "First month on us" is a monthly-shaped
 * claim (spec §3.5) — a full year isn't a month, so it must never render on
 * the annual lane. Annual's honest promise is the amount due TODAY: the
 * effective (post-discount) annual price minus the promised starter credit,
 * floored at $0.
 *
 * Returns `null` when there is no confirmed promise to render
 * (`starterPromisedCents <= 0`) — never a fabricated "$0 due today" and never
 * a monthly-shaped claim ported onto the annual price.
 */
export function starterActivationCopyAnnual(
  activation: CRMBillingActivationVM,
  starterPromisedCents: number,
): { subtitle: string; body: string } | null {
  if (starterPromisedCents <= 0) return null;
  // Already net of the credit and floored server-side — this file states the
  // figure, it does not compute it.
  const dueToday = money(activation.annualDueTodayCents);
  const clause = activation.discountActive
    ? `${activation.discountPercent}% off + one month credited`
    : "one month credited";
  return {
    subtitle: `${dueToday} due today (${clause})`,
    body: `Sill Pro — ${money(activation.effectiveAnnualCents)}/yr, ${dueToday} due today (${clause}).`,
  };
}
